import { ActiveEW, AdminLevel, BCONXResponseType, UkAdminCollection } from "./LocationMapTypes";

const BASE_URL = process.env.REACT_APP_BASE_URL
const API = `${BASE_URL}/map/v1/level`

/**
 * Where map areas are loaded from:
 *  - "api"  : backend  GET /map/v1/level/{level}
 *  - "json" : static files  /public/geoJSON/areas/uk-level-{level}.json
 *  - "auto" : try the API first, use the JSON file if it fails
 * Set REACT_APP_MAP_SOURCE in .env (default "api").
 */
export type MapDataSource = "api" | "json" | "auto"

const ENV_SOURCE = (process.env.REACT_APP_MAP_SOURCE ?? "api").toLowerCase() as MapDataSource

// optional override at runtime, e.g. from a settings switch
let currentSource: MapDataSource = ["api", "json", "auto"].includes(ENV_SOURCE) ? ENV_SOURCE : "api"

export function setMapDataSource(source: MapDataSource)
{
    currentSource = source
}

export function getMapDataSource(): MapDataSource
{
    return currentSource
}

function jsonUrl(level: AdminLevel)
{
    return `${process.env.PUBLIC_URL ?? ""}/geoJSON/areas/uk-level-${level}.json`
}

async function loadFrom(url: string, level: AdminLevel, signal?: AbortSignal): Promise<UkAdminCollection>
{
    const res = await fetch(url, { signal })
    if (!res.ok) throw new Error(`Failed to load level ${level} from ${url}: HTTP ${res.status}`)
    return (await res.json()) as UkAdminCollection
}

export async function fetchUkLevel(level: AdminLevel, signal?: AbortSignal, source: MapDataSource = currentSource): Promise<UkAdminCollection>
{
    if (source === "json")
    {
        return loadFrom(jsonUrl(level), level, signal)
    }

    if (source === "auto")
    {
        try
        {
            return await loadFrom(`${API}/${level}`, level, signal)
        }
        catch (err)
        {
            if (signal?.aborted) throw err
            console.warn(`Map API failed for level ${level}, using JSON file`, err)
            return loadFrom(jsonUrl(level), level, signal)
        }
    }

    return loadFrom(`${API}/${level}`, level, signal)
}

export async function getActiveEarlyWarning(signal?: AbortSignal): Promise<ActiveEW[]>
{
    // read the token on every call, so a fresh login is picked up
    const res = await fetch(`${BASE_URL}/ew/api/v1/get_ew_locations`, {
        signal,
        headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
        },
    })
    if (!res.ok) throw new Error(`EW loading failed: HTTP ${res.status}`)
    const data = (await res.json()) as BCONXResponseType
    return (data.data ?? []) as ActiveEW[]
}
